import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Geolocation } from "@ionic-native/geolocation/ngx";
//import { Storage } from "@ionic/storage";
import { AndroidPermissions } from "@ionic-native/android-permissions/ngx";
import { LocationAccuracy } from "@ionic-native/location-accuracy/ngx";
import { Platform } from "@ionic/angular";
import { Common } from "./model/Common";
import { AddressByGeoLocation } from "./model/Response/AddressByGeoLocation";
import { catchError, map } from 'rxjs/operators';
import { Observable } from "rxjs/Observable";
import { throwError } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class GeoLocationService {
  addressInfo: AddressByGeoLocation = {};

  constructor(
    private http: HttpClient,
    private geolocation: Geolocation,
    //private storage: Storage,
    private androidPermissions: AndroidPermissions,
    private locationAccuracy: LocationAccuracy,
    private platform: Platform
  ) {
    this.checkLocationPermission();
  }

  async checkLocationPermission() {
    if (this.platform.is("cordova") || this.platform.is("capacitor")) {
      try {
        const result = await this.androidPermissions.checkPermission(
          this.androidPermissions.PERMISSION.ACCESS_FINE_LOCATION
        );

        if (result.hasPermission) {
          // alert("GPS permission is already granted");
          await this.getUserCurrentLocation();
        } else {
          // alert("GPS permission is not granted, requesting permission...");
          await this.requestGPSPermission();
        }
      } catch (err) {
        // alert("Error checking location permission: " + err);
        this.requestGPSPermission(); // Request permission if check fails
      }
    } else {
      this.mockLocationForBrowser();
    }
  }

  async requestGPSPermission() {
    const canRequest = await this.locationAccuracy.canRequest();
    // alert("requestGPSPermission +canRequest value " + canRequest);

    if (!canRequest) {
      // alert("requestGPSPermission +!canRequest value " + !canRequest);
      // Show 'GPS Permission Request' dialogue
      try {
        await this.androidPermissions.requestPermission(
          this.androidPermissions.PERMISSION.ACCESS_FINE_LOCATION
        );
        await this.askToTurnOnGPS();
      } catch (error) {
        // alert("Failed to request GPS permission: " + error);
      }
    } else {
      await this.askToTurnOnGPS();
    }
  }

  async askToTurnOnGPS() {
    // alert("askToTurnOnGPS");
    try {
      await this.locationAccuracy.request(
        this.locationAccuracy.REQUEST_PRIORITY_HIGH_ACCURACY
      );
      await this.getUserCurrentLocation();
    } catch (error) {
      // alert("Failed to enable GPS: " + error);
    }
  }

  async getUserCurrentLocation() {
    // alert("getUserCurrentLocation called");

    try {
      const position = await this.geolocation.getCurrentPosition();
      await this.reverseGeocode(
        position.coords.latitude,
        position.coords.longitude
      );
    } catch (error) {
      // console.error("Error getting user location: ", error);
      // alert("Failed to get current location: " + error.message);
    }
  }

  async reverseGeocode(lat: number, lon: number) {
    // alert("reverseGeocode: " + lat + ", " + lon);
    const apiUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lon}&key=${Common.GOOGLE_MAPS_ANDROID_API_KEY}`;

    try {
      const response: any = await this.http.get(apiUrl).toPromise();
      if (response.results && response.results.length > 0) {
        const components = response.results[0].address_components;
        this.addressInfo.city = this.getComponent(components, "locality");
        this.addressInfo.state = this.getComponent(
          components,
          "administrative_area_level_1"
        );
        this.addressInfo.country = this.getComponent(
          components,
          "country"
        );
        this.addressInfo.zipcode = this.getComponent(
          components,
          "postal_code"
        );
        this.addressInfo.let = lat;
        this.addressInfo.long = lon;
        this.addressInfo.fullAddressInfo =
          response.results[0].formatted_address;

        // alert("Address: " + this.addressInfo.fullAddressInfo);
        await this.setAddressInfo(this.addressInfo);
      } else {
        // alert("No results found for the provided location.");
      }
    } catch (error) {
      // console.error("Error fetching city and state", error);
      // alert("Error fetching address information: " + error.message);
    }
  }

  getComponent(components: any[], type: string) {
    return components.find((c) => c.types.indexOf(type) > -1)?.long_name || "";
  }

  async setAddressInfo(address: AddressByGeoLocation) {
    this.addressInfo = address;
    localStorage.setItem("addressInfo", JSON.stringify(this.addressInfo));
    //await this.storage.set("addressInfo", address);
  }

  async getAddressInfo(): Promise<AddressByGeoLocation> {
    // Check if addressInfo is not set or is an empty object
    if (!this.addressInfo || Object.keys(this.addressInfo).length === 0) {
      const storedAddress = localStorage.get("addressInfo");
      if (storedAddress) {
        this.addressInfo = storedAddress;
      }
    }
    return this.addressInfo;
  }

  async mockLocationForBrowser() {
    //alert("mockLocationForBrowser, requesting permission...");

    const position = await this.geolocation.getCurrentPosition();

    this.reverseGeocode(position.coords.latitude, position.coords.longitude);
  }

  getLatLongFromAddress(address: string): Observable<{ latitude: number; longitude: number } | null> {
    const url = `${Common.geocodeUrl}?address=${encodeURIComponent(address)}&key=${Common.GOOGLE_MAPS_ANDROID_API_KEY}`;
    return this.http.get<any>(url).pipe(
      map((response) => {
        if (response.status === 'OK' && response.results.length > 0) {
          const location = response.results[0].geometry.location;
          return { latitude: location.lat, longitude: location.lng };
        } else {
          return null;  // Return null if coordinates are not found
        }
      }),
      catchError((error) => throwError(() => new Error(error.message)))
    );
  }
}
